// Styles
import styled from 'styled-components/macro'

// Core
import React, {ReactNode, useEffect, useState} from 'react'
import {useSelector} from 'react-redux'
import {Route, useRouteMatch} from 'react-router'
import {Link as RouterLink, useParams} from 'react-router-dom'

// Components, services, etc
import ClearCapitalLogo from 'assets/logo-small.svg'
import Icon from 'components/Icon'
import {LOADING, UNSET} from 'constants/reduxStatuses'
import {
  isUserAuthenticatedSelector,
  propertyDataAddressSelector,
  propertyStatusSelector
} from 'selectors'
import {titleCase} from 'services/formats'

// 3rd-party
import Breadcrumbs from '@material-ui/core/Breadcrumbs'
import Link from '@material-ui/core/Link'
import Typography from '@material-ui/core/Typography'
import {ALL_ORDERS_PATH, PropertiesRoute} from 'constants/appRoutesConstants'
import {productDataDictionary} from 'constants/productCardConstants'
import {usePropertyData} from 'hooks/usePropertyData'
import type {ProductType} from 'types/orderTypes'

type LinkRouterProps = {
  color?:
    | 'inherit'
    | 'primary'
    | 'secondary'
    | 'error'
    | 'initial'
    | 'textPrimary'
    | 'textSecondary'
  to: string
  children?: ReactNode
}
const LinkRouter = (props: LinkRouterProps) => <Link {...props} component={RouterLink} />

type PathnameType = {last?: boolean; to: string; label?: string}

const HeaderBreadcrumbs = () => {
  const [pathnames, setPathnames] = useState<PathnameType[]>([])
  const {path, url} = useRouteMatch()
  const addressData = useSelector(propertyDataAddressSelector)
  const addressStatus = useSelector(propertyStatusSelector)
  const addressLoading = addressStatus === LOADING || addressStatus === UNSET
  const isLoggedIn = useSelector(isUserAuthenticatedSelector)

  const {pid: pidInUrl} = useParams<PropertiesRoute>()
  usePropertyData(pidInUrl)

  useEffect(() => {
    const paths = path.split('/').filter(x => x && x !== ':opsOrderId' && x !== 'redirect')
    if (paths[0] !== 'social-auth-tokens') {
      const pathParts = addressLoading && paths[0] === 'properties' ? [] : paths
      const urlParts = url.split('/').filter(x => x)
      let newPathNames: PathnameType[]
      switch (pathParts[0]) {
        case 'properties':
          if (!isLoggedIn) {
            pathParts.shift()
          }
          newPathNames = pathParts.map((pathPart, index) => {
            const last = index === pathParts.length - 1
            const to =
              pathPart === 'properties'
                ? ALL_ORDERS_PATH
                : `/${urlParts.slice(0, index + 1).join('/')}`
            let label: string | undefined
            switch (pathPart) {
              case 'clearprop':
              case 'clearavm':
              case 'cda':
                label = productDataDictionary[pathPart.toUpperCase() as ProductType].chipLabel
                break
              case ':pid':
                label = addressData?.displayAddressLine1
                break
              default:
                label = titleCase(pathPart)
            }
            return {
              last,
              to,
              label
            }
          })
          break
        case 'orders':
          newPathNames = [{last: true, to: '/', label: 'All Properties'}]
          break
        default:
          newPathNames = pathParts.map((pathPart, index) => {
            const last = index === pathParts.length - 1
            const to = `/${urlParts.slice(0, index + 1).join('/')}`
            const label = titleCase(pathPart)
            return {
              last,
              to,
              label
            }
          })
      }
      setPathnames(newPathNames)
    }
  }, [addressData, addressLoading, isLoggedIn, path, url])

  return (
    <Route>
      {() => {
        return (
          <HeaderBreadcrumbs.Styled
            aria-label='breadcrumb'
            separator={<Icon icon='navigate_next' />}
          >
            <LinkRouter color='inherit' to='/'>
              <img src={ClearCapitalLogo} alt='Clear Capital Logo' />
            </LinkRouter>
            {pathnames.map(({last, to, label}) => {
              return last ? (
                <Typography color='textPrimary' key={to}>
                  {label}
                </Typography>
              ) : (
                <LinkRouter color='inherit' to={to} key={to}>
                  {label}
                </LinkRouter>
              )
            })}
          </HeaderBreadcrumbs.Styled>
        )
      }}
    </Route>
  )
}

HeaderBreadcrumbs.Styled = styled(Breadcrumbs)`
  img {
    max-height: 24px;
  }
  .portal-MuiBreadcrumbs-li {
    font-size: 16px;
    line-height: 22px;
    letter-spacing: -0.3px;

    .portal-MuiLink-root {
      font-weight: 500;
      color: ${({theme}) => theme.colors.grayscale.black};
      display: flex;
    }

    .portal-MuiTypography-body1 {
      color: ${({theme}) => theme.colors.grayscale.gray};
    }
  }

  .portal-MuiBreadcrumbs-separator {
    color: ${({theme}) => theme.colors.stone.base};
    margin-left: 4px;
    margin-right: 4px;
  }
`

export default HeaderBreadcrumbs
